"use client"

import { motion } from "framer-motion"
import { Brain, Zap, Target, Users, Award, Sparkles } from "lucide-react"

export default function AboutView() {
  const features = [
    {
      icon: Brain,
      title: "Advanced AI Analysis",
      description: "Powered by cutting-edge artificial intelligence to analyze brain activity patterns in real-time"
    },
    {
      icon: Zap,
      title: "Real-Time Insights",
      description: "Get instant feedback on cognitive processes and neural activity as you perform different tasks"
    },
    {
      icon: Target,
      title: "Precision Mapping",
      description: "Accurately map brain regions and their functions with our 3D visualization technology"
    },
    {
      icon: Users,
      title: "User-Friendly",
      description: "Designed with simplicity in mind, making complex neuroscience accessible to everyone"
    }
  ]

  const team = [
    {
      role: "Mission",
      title: "Democratizing Neuroscience",
      description: "Making brain science accessible and understandable for everyone through innovative technology"
    },
    {
      role: "Vision",
      title: "Future of Brain Health",
      description: "Empowering individuals to understand and optimize their cognitive performance"
    },
    {
      role: "Values",
      title: "Innovation & Accuracy",
      description: "Combining cutting-edge AI with scientifically validated neuroscience principles"
    }
  ]

  return (
    <div className="about-view">
      {/* Hero Section */}
      <motion.div
        className="about-hero"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="about-hero-content">
          <motion.div
            className="about-icon-wrapper"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
          >
            <Brain size={80} className="about-hero-icon" />
          </motion.div>
          <h1 className="about-title">
            About <span className="gradient-text">NeuroEase</span>
          </h1>
          <p className="about-subtitle">
            Advanced Brain Activity Analysis & Visualization Platform
          </p>
        </div>
      </motion.div>

      {/* Description Section */}
      <motion.div
        className="about-description glass-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h2>What is NeuroEase?</h2>
        <p>
          NeuroEase is an innovative platform that combines artificial intelligence with neuroscience 
          to help you understand how your brain works. Through interactive 3D visualizations and 
          real-time analysis, we make complex brain activity patterns accessible and easy to understand.
        </p>
        <p>
          Whether you're exploring how different activities affect your brain, learning about cognitive 
          processes, or tracking your mental wellness, NeuroEase provides the tools and insights you need.
        </p>
      </motion.div>

      {/* Features Grid */}
      <motion.div
        className="about-features"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <h2 className="section-title">Key Features</h2>
        <div className="features-grid">
          {features.map((feature, index) => {
            const Icon = feature.icon
            return (
              <motion.div
                key={index}
                className="feature-card glass-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon-wrapper">
                  <Icon size={40} className="feature-icon" />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            )
          })}
        </div>
      </motion.div>

      {/* Mission/Vision/Values */}
      <motion.div
        className="about-mission"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <h2 className="section-title">Our Purpose</h2>
        <div className="mission-grid">
          {team.map((item, index) => (
            <motion.div
              key={index}
              className="mission-card glass-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="mission-badge">{item.role}</div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Technology Section */}
      <motion.div
        className="about-tech glass-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
      >
        <div className="tech-header">
          <Sparkles size={32} className="tech-icon" />
          <h2>Powered by Advanced Technology</h2>
        </div>
        <div className="tech-content">
          <div className="tech-item">
            <Award size={24} />
            <div>
              <h4>AI-Powered Analysis</h4>
              <p>Machine learning algorithms trained on neuroscience research</p>
            </div>
          </div>
          <div className="tech-item">
            <Brain size={24} />
            <div>
              <h4>3D Brain Visualization</h4>
              <p>Interactive models built with Three.js and React Three Fiber</p>
            </div>
          </div>
          <div className="tech-item">
            <Zap size={24} />
            <div>
              <h4>Real-Time Processing</h4>
              <p>Instant analysis and feedback on cognitive activities</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div
        className="about-cta"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <h2>Ready to Explore Your Brain?</h2>
        <p>Start analyzing brain activity patterns and discover how your mind works</p>
        <motion.button
          className="cta-button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Get Started
        </motion.button>
      </motion.div>
    </div>
  )
}
